import { VFC } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import { splitEmailAddresses } from 'dashboard/forms/FormSendCaseEmail/utils';
import { CaseEmailTemplate } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormEditCaseEmailTemplateProps {
  defaultValues: FormEditCaseEmailTemplateData;
  handleUpdateEmailTemplate: (updatedEmailTemplate: CaseEmailTemplate) => void;
  handleClose: () => void;
}

const FormEditCaseEmailTemplate: VFC<FormEditCaseEmailTemplateProps> = ({
  defaultValues,
  handleUpdateEmailTemplate,
  handleClose,
}) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<FormEditCaseEmailTemplateData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(handleUpdateEmailTemplate)}>
      <Label id='form-edit-case-email-template__name_label' size='md' weight='bold'>
        Template name
      </Label>
      <Input
        {...register('name', {
          required: 'Email templates must have a name.',
          pattern: {
            value: /^[_a-zA-Z0-9][_a-zA-Z0-9\s]*$/,
            message: 'Please use letters, numbers, underscores, or spaces only.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-edit-case-email-template__name_label'
      />
      {errors['name'] && <FormError>{errors['name'].message}</FormError>}
      <Spacer height={1} />
      <Label id='form-edit-case-email-template__cc_label' size='md' weight='bold'>
        Cc
      </Label>
      <Controller
        control={control}
        name='cc'
        render={({ field: { onChange, value } }) => (
          <ReactCreatableSelect
            placeholder='Start typing to add Cc email addresses'
            value={value}
            disabled={isSubmitting}
            options={(value ?? []).map((val) => ({
              label: val,
              value: val,
            }))}
            portal={true}
            isMulti={true}
            isClearable={true}
            onChange={(values: string[]) => {
              onChange(splitEmailAddresses(values));
            }}
            aria-labelledby='form-edit-case-email-template__cc_label'
          />
        )}
      />
      <Spacer height={1} />
      <Label id='form-edit-case-email-template__subject_label' size='md' weight='bold'>
        Subject
      </Label>
      <Input
        {...register('subject')}
        placeholder='Subject'
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-edit-case-email-template__subject_label'
      />
      <Spacer height={1} />
      <Label size='md' weight='bold'>
        Body
      </Label>
      <TextArea
        {...register('body')}
        disabled={isSubmitting}
        placeholder='Compose your message here...'
      />
      <Spacer height={2} />
      <HStack equal={true}>
        <Button
          id='form-edit-case-email-template__cancel_button'
          variant='basic'
          disabled={isSubmitting}
          isPending={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          type='submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Update
        </Button>
      </HStack>
    </form>
  );
};

export interface FormEditCaseEmailTemplateData {
  name: string;
  token: string;
  'creator-id': string;
  cc: string[];
  subject: string;
  body: string;
}

export default FormEditCaseEmailTemplate;
